import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import {
  FontText,
  PrimaryButton,
  SecondaryButton,
  SEOHead,
} from '../components/common';
import { localAnalytics } from '../utils/analytics';

export const NotFound: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localAnalytics().logEvent('404PageViewed', {
      screen: 'NotFound',
      action: 'PageViewed',
      path: location.pathname,
    });
  }, [location.pathname]);

  const handleGoHome = () => {
    localAnalytics().logEvent('404GoHomeClicked', {
      screen: 'NotFound',
      action: 'GoHomeClicked',
      path: location.pathname,
    });
    navigate('/');
  };

  const handleGoBack = () => {
    localAnalytics().logEvent('404GoBackClicked', {
      screen: 'NotFound',
      action: 'GoBackClicked',
      path: location.pathname,
    });
    window.history.back();
  };

  const handleExploreBooks = () => {
    localAnalytics().logEvent('404ExploreBooksClicked', {
      screen: 'NotFound',
      action: 'ExploreBooksClicked',
      path: location.pathname,
    });
    navigate('/');
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      padding: '40px 20px',
      textAlign: 'center',
    },
    content: {
      maxWidth: 600,
      width: '100%',
    },
    errorCode: {
      color: theme.colors.accent,
      marginBottom: 20,
      fontSize: 120,
      fontWeight: 'bold',
      lineHeight: 1,
    },
    title: {
      color: theme.colors.text,
      marginBottom: 16,
    },
    message: {
      color: theme.colors.textSecondary,
      marginBottom: 40,
      lineHeight: 1.6,
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      alignItems: 'center',
      width: '100%',
    },
    buttonRow: {
      display: 'flex',
      gap: 16,
      flexWrap: 'wrap',
      justifyContent: 'center',
      width: '100%',
    },
    button: {
      minWidth: 140,
    },
  };

  return (
    <>
      <SEOHead
        title={t('page_not_found_title')}
        description={t('page_not_found_description')}
        canonical="/404"
      />
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.errorCode}>404</div>
          <FontText h1 style={styles.title}>
            {t('page_not_found')}
          </FontText>
          <FontText normal style={styles.message}>
            {t('page_not_found_message')}
          </FontText>
          <div style={styles.buttonContainer}>
            <div style={styles.buttonRow}>
              <PrimaryButton onClick={handleGoHome} style={styles.button}>
                {t('back_to_home')}
              </PrimaryButton>
              <SecondaryButton onClick={handleGoBack} style={styles.button}>
                {t('go_back')}
              </SecondaryButton>
            </div>
            <SecondaryButton onClick={handleExploreBooks} style={styles.button}>
              {t('explore_books')}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </>
  );
};
